import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router-dom';
import { RouterProvider } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import PollingTollingAdminAccess from 'components/PollingTollingAdminAccess.tsx';
import React, { Suspense, useEffect, useState } from 'react';

import PageLayout from 'components/PageLayout';
import { Pages } from 'utils/pages.utils';

import useAuthStore from 'store/authStore';
import ProtectedRoute from 'components/ProtectedRoute';
import { PAGES_ENUM, USER_ROLES_ENUM } from 'utils/authenticationUtils';
import LoginPage from 'components/LoginPage';
import LoadingPage from 'components/LoadingPage';
import HomePage from 'components/HomePage';
import NotFoundPage from 'components/NotFoundPage';

// manual chunks defined in vite.config.ts
const BillingDetailsPage = React.lazy(() => import('./pages/BillingDetailsPage'));
const BillingDetailsListPage = React.lazy(() => import('./pages/BillingDetailsListPage'));

const TollDeclarationsPage = React.lazy(() => import('./pages/TollDeclarationsPage'));
const TollDeclarationsListPage = React.lazy(() => import('./pages/TollDeclarationsListPage'));

const PaymentClaimsPage = React.lazy(() => import('./pages/PaymentClaimsPage'));
const PaymentClaimsListPage = React.lazy(() => import('./pages/PaymentClaimsListPage'));

const AuditLogListPage = React.lazy(() => import('./pages/AuditLogListPage'));

const ObeEventsPage = React.lazy(() => import('./pages/ObeEventsPage'));
const ObeKapschEventsPage = React.lazy(() => import('./pages/ObeKapschEventsPage'));
const ObeFineHelperPage = React.lazy(() => import('./pages/ObeFineHelperPage'));

const AgreementLineListPage = React.lazy(() => import('./pages/AgreementLineListPage'));
const AgreementLineDetailsPage = React.lazy(() => import('./pages/AgreementLineDetailsPage'));

const AssetManagementListPage = React.lazy(() => import('./pages/AssetManagementListPage'));
const AssetManagementDetailsPage = React.lazy(() => import('./pages/AssetManagementDetailsPage'));

const EfcContextDataInboundPage = React.lazy(() => import('./pages/EfcContextDataInboundPage'));
const EfcContextDataOutboundPage = React.lazy(() => import('./pages/EfcContextDataOutboundPage'));
const OresundPayDetailsPage = React.lazy(() => import('./pages/OresundPayDetailsPage'));
const OresundPayEditPage = React.lazy(() => import('./pages/OresundPayEditPage'));

const InboundStatusAduListPage = React.lazy(() => import('./pages/InboundStatusAduListPage'));

const EfcContextMarksListPage = React.lazy(() => import('./pages/EfcContextMarkListPage'));
const EfcContextMarkDetailsPage = React.lazy(() => import('./pages/EfcContextMarkDetailsPage'));
const EfcContextMarkEditPage = React.lazy(() => import('./pages/EfcContextMarkEditPage'));
const EfcContextMarkNewPage = React.lazy(() => import('./pages/EfcContextMarksNewPage'));

const ExceptionListsPage = React.lazy(() => import('./pages/ExceptionListsPage'));
const ExceptionListDetailsPage = React.lazy(() => import('./pages/ExceptionListDetailsPage'));

const ContractIssuerListPage = React.lazy(() => import('./pages/ContractIssuerListPage'));
const ContractIssuerDetailsPage = React.lazy(() => import('./pages/ContractIssuerDetailsPage'));

const TransactionsListPage = React.lazy(() => import('./pages/TransactionsListPage'));
const TransactionsDetailsPage = React.lazy(() => import('./pages/TransactionsDetailsPage'));

const TollChargersListPage = React.lazy(() => import('./pages/TollChargersListPage'));
const TollChargerDetailsPage = React.lazy(() => import('./pages/TollChargerDetailsPage'));
const TollChargerEditPage = React.lazy(() => import('./pages/TollChargerEditPage'));
const TollChargerNewPage = React.lazy(() => import('./pages/TollChargerNewPage'));

const AbnormalBehaviorReportListPage = React.lazy(
  () => import('./pages/AbnormalBehaviorReportListPage')
);

const AdminPage = React.lazy(() => import('./pages/AdminPage'));
const AdminUserGroupsPage = React.lazy(() => import('./pages/AdminUserGroupsPage'));

const LazyProtectedRoute = ({
  Component,
  page,
  role,
}: {
  Component: React.LazyExoticComponent<() => React.JSX.Element>;
  page?: PAGES_ENUM;
  role?: USER_ROLES_ENUM;
}) => (
  <Suspense fallback={<LoadingPage />}>
    <ProtectedRoute page={page} role={role}>
      <Component />
    </ProtectedRoute>
  </Suspense>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path={Pages.home} element={<PageLayout />}>
      <Route index element={<HomePage />} />

      <Route
        path={Pages.billingDetailsList}
        element={
          <LazyProtectedRoute Component={BillingDetailsListPage} page={PAGES_ENUM.billingDetails} />
        }
      />
      <Route
        path={Pages.billingDetails}
        element={
          <LazyProtectedRoute Component={BillingDetailsPage} page={PAGES_ENUM.billingDetails} />
        }
      />

      <Route
        path={Pages.agreementLineList}
        element={
          <LazyProtectedRoute Component={AgreementLineListPage} page={PAGES_ENUM.agreementLines} />
        }
      />

      <Route
        path={Pages.agreementLineDetails}
        element={
          <LazyProtectedRoute
            Component={AgreementLineDetailsPage}
            page={PAGES_ENUM.agreementLines}
          />
        }
      />

      <Route
        path={Pages.tollDeclarationsList}
        element={
          <LazyProtectedRoute
            Component={TollDeclarationsListPage}
            page={PAGES_ENUM.tollDeclarations}
          />
        }
      />
      <Route
        path={Pages.tollDeclarations}
        element={
          <LazyProtectedRoute Component={TollDeclarationsPage} page={PAGES_ENUM.tollDeclarations} />
        }
      />
      <Route
        path={Pages.paymentClaimsList}
        element={
          <LazyProtectedRoute Component={PaymentClaimsListPage} page={PAGES_ENUM.paymentClaims} />
        }
      />
      <Route
        path={Pages.paymentClaims}
        element={
          <LazyProtectedRoute Component={PaymentClaimsPage} page={PAGES_ENUM.paymentClaims} />
        }
      />

      <Route
        path={Pages.auditLog}
        element={<LazyProtectedRoute Component={AuditLogListPage} page={PAGES_ENUM.auditLog} />}
      />

      <Route
        path={Pages.obeEventLog}
        element={<LazyProtectedRoute Component={ObeEventsPage} page={PAGES_ENUM.obeEventLog} />}
      />

      <Route
        path={Pages.obeKapschEventLog}
        element={
          <LazyProtectedRoute Component={ObeKapschEventsPage} page={PAGES_ENUM.obeEventLog} />
        }
      />

      <Route
        path={Pages.obeFineHelper}
        element={<LazyProtectedRoute Component={ObeFineHelperPage} page={PAGES_ENUM.obeEventLog} />}
      />

      <Route
        path={Pages.efcContextDataInbound}
        element={
          <LazyProtectedRoute
            Component={EfcContextDataInboundPage}
            page={PAGES_ENUM.efcContextData}
          />
        }
      />
      <Route
        path={Pages.efcContextDataOutbound}
        element={
          <LazyProtectedRoute
            Component={EfcContextDataOutboundPage}
            page={PAGES_ENUM.efcContextData}
          />
        }
      />

      <Route
        path={Pages.statusAduListInbound}
        element={
          <LazyProtectedRoute
            Component={InboundStatusAduListPage}
            page={PAGES_ENUM.inboundStatusAdu}
          />
        }
      />

      <Route
        path={Pages.oresundPayData}
        element={
          <LazyProtectedRoute Component={OresundPayDetailsPage} page={PAGES_ENUM.oresundPayData} />
        }
      />
      <Route
        path={Pages.oresundPayDataEdit}
        element={
          <LazyProtectedRoute Component={OresundPayEditPage} page={PAGES_ENUM.oresundPayData} />
        }
      />

      <Route
        path={Pages.exceptionLists}
        element={
          <LazyProtectedRoute Component={ExceptionListsPage} page={PAGES_ENUM.exceptionLists} />
        }
      />
      <Route
        path={Pages.exceptionListDetails}
        element={
          <LazyProtectedRoute
            Component={ExceptionListDetailsPage}
            page={PAGES_ENUM.exceptionLists}
          />
        }
      />

      <Route
        path={Pages.efcContextMarksList}
        element={
          <LazyProtectedRoute
            Component={EfcContextMarksListPage}
            page={PAGES_ENUM.efcContextMarks}
          />
        }
      />

      <Route
        path={Pages.efcContextMarkDetails}
        element={
          <LazyProtectedRoute
            Component={EfcContextMarkDetailsPage}
            page={PAGES_ENUM.efcContextMarks}
          />
        }
      />

      <Route
        path={Pages.efcContextMarkNew}
        element={
          <LazyProtectedRoute Component={EfcContextMarkNewPage} page={PAGES_ENUM.efcContextMarks} />
        }
      />

      <Route
        path={Pages.efcContextMarkEdit}
        element={
          <LazyProtectedRoute
            Component={EfcContextMarkEditPage}
            page={PAGES_ENUM.efcContextMarks}
          />
        }
      />

      <Route
        path={Pages.tollChargersList}
        element={
          <LazyProtectedRoute Component={TollChargersListPage} page={PAGES_ENUM.tollChargers} />
        }
      />

      <Route
        path={Pages.tollChargerDetails}
        element={
          <LazyProtectedRoute Component={TollChargerDetailsPage} page={PAGES_ENUM.tollChargers} />
        }
      />

      <Route
        path={Pages.tollChargerEdit}
        element={
          <LazyProtectedRoute Component={TollChargerEditPage} page={PAGES_ENUM.tollChargers} />
        }
      />

      <Route
        path={Pages.tollChargerNew}
        element={
          <LazyProtectedRoute Component={TollChargerNewPage} page={PAGES_ENUM.tollChargers} />
        }
      />

      <Route
        path={Pages.assetManagementList}
        element={
          <LazyProtectedRoute
            Component={AssetManagementListPage}
            page={PAGES_ENUM.assetManagementList}
          />
        }
      />

      <Route
        path={Pages.assetManagementDetails}
        element={
          <LazyProtectedRoute
            Component={AssetManagementDetailsPage}
            page={PAGES_ENUM.assetManagementDetails}
          />
        }
      />

      <Route
        path={Pages.adminPage}
        element={<LazyProtectedRoute Component={AdminPage} role={USER_ROLES_ENUM.userAdminGroup} />}
      />
      <Route
        path={Pages.adminUserGroups}
        element={
          <LazyProtectedRoute
            Component={AdminUserGroupsPage}
            role={USER_ROLES_ENUM.userAdminGroup}
          />
        }
      />

      <Route
        path={Pages.contractIssuerList}
        element={
          <LazyProtectedRoute
            Component={ContractIssuerListPage}
            page={PAGES_ENUM.contractIssuerList}
          />
        }
      />
      <Route
        path={Pages.contractIssuerDetails}
        element={
          <LazyProtectedRoute
            Component={ContractIssuerDetailsPage}
            page={PAGES_ENUM.contractIssuerList}
          />
        }
      />

      <Route
        path={Pages.transactionsList}
        element={
          <LazyProtectedRoute Component={TransactionsListPage} page={PAGES_ENUM.transactions} />
        }
      />
      <Route
        path={Pages.transactionsDetails}
        element={
          <LazyProtectedRoute Component={TransactionsDetailsPage} page={PAGES_ENUM.transactions} />
        }
      />

      <Route
        path={Pages.abnormalBehaviorReportList}
        element={
          <LazyProtectedRoute
            Component={AbnormalBehaviorReportListPage}
            page={PAGES_ENUM.abnormalBehaviorReports}
          />
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Route>
  )
);

function Router() {
  const [signedInRoutineDone, setSignedInRoutineDone] = useState<boolean>(false);
  const { acquireToken, acquireTokenSso } = useAuthStore();

  useEffect(() => {
    // Fetch token when app start
    void (async () => {
      const token = await acquireToken();
      if (import.meta.env.MODE !== 'production') {
        console.log('token', token);
      }
      if (!token) {
        const token = await acquireTokenSso();
        if (import.meta.env.MODE !== 'production') {
          console.log('SSO token', token);
        }
      }
      setSignedInRoutineDone(true);
    })();
  }, [acquireToken, acquireTokenSso]);

  if (!signedInRoutineDone) {
    return null;
  }

  return (
    <>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <PollingTollingAdminAccess />
        <RouterProvider router={router} />
      </AuthenticatedTemplate>
      {import.meta.env.VITE_ENVIRONMENT !== 'production' && (
        <>
          <div className="fixed top-0 right-0 h-6 border bg-white px-1 opacity-50">
            {import.meta.env.VITE_ENVIRONMENT}
          </div>
          <div className="fixed top-6 right-0 h-6 border bg-white px-1 opacity-50">
            <div className="xs:visible sm:hidden">XS</div>
            <div className="max-sm:hidden sm:visible md:hidden">SM</div>
            <div className="max-md:hidden md:visible lg:hidden">MD</div>
            <div className="max-lg:hidden lg:visible xl:hidden">LG</div>
            <div className="max-xl:hidden xl:visible 2xl:hidden">XL</div>
            <div className="3xl:hidden max-2xl:hidden 2xl:visible">2XL</div>
            <div className="max-3xl:hidden 3xl:visible 4xl:hidden">3XL</div>
            <div className="max-4xl:hidden 4xl:visible 5xl:hidden">4XL</div>
            <div className="6xl:hidden max-5xl:hidden 5xl:visible">5XL</div>
          </div>
        </>
      )}
    </>
  );
}

export default Router;
